// pages/orders/OrderList.jsx
import { useEffect, useState } from "react";
import { ordersAPI }    from "../../services/api";
import { useRouter }    from "../../contexts/RouterContext";
import { useToast }     from "../../contexts/ToastContext";
import { usePagination } from "../../hooks/usePagination";
import { Loading, Pagination, ConfirmModal, EmptyState } from "../../components/ui";
import { fmt, StatusBadge } from "../../utils/helpers";
import ReceiptModal       from "./ReceiptModal";

export default function OrderList() {
  const { navigate } = useRouter();
  const toast        = useToast();

  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [filterStatus,  setFilterStatus]  = useState("All");
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  const load = () => {
    setLoading(true);
    ordersAPI.list().then((d) => { setOrders(d); setLoading(false); });
  };
  useEffect(load, []);

  const filtered = orders.filter((o) => {
  const searchText = search.toLowerCase();

  const matchSearch =
    (o.customerName ?? "").toLowerCase().includes(searchText) ||
    String(o.id ?? "").includes(searchText);

  const matchStatus = filterStatus === "All" || o.status === filterStatus;

  return matchSearch && matchStatus;
});
  const { paged, page, setPage, total, count } = usePagination(filtered, 8);

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const handleComplete = async (o) => {
    await ordersAPI.complete(o.id);
    toast(`Order ${o.id} marked complete! 🎉`);
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget; // capture before clearing state

    setDeleting(true);
    try {
      await ordersAPI.delete(id);
      toast(`Order ${id} deleted`);
      load();
    } catch (e) {
      console.error(e);
      // Remove_Order.php explains refusals precisely (payment received, partially
      // delivered, karigar assigned) — show that rather than a generic failure.
      toast(e.message || "Failed to delete order", "error");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Orders</h1>
          <p className="page-sub">{orders.length} order{orders.length !== 1 ? "s" : ""} total</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate("order-create")}>
          ＋ New Order
        </button>
      </div>

      {/* Quick status filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          ["All",         `All (${orders.length})`],
          ["Pending",     `Pending (${statusCounts["Pending"]     || 0})`],
          ["Completed",   `Completed (${statusCounts["Completed"]  || 0})`],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`btn btn-sm${filterStatus === key ? " btn-primary" : " btn-ghost"}`}
            onClick={() => setFilterStatus(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
            <span className="search-icon">🔍</span>
            <input
              className="form-input"
              placeholder="Search order ID or customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {search && <button className="btn btn-ghost btn-sm" onClick={() => setSearch("")}>✕ Clear</button>}
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer Name</th>
                    <th>Order Date</th>
                    {/* <th>Delivery Date</th> */}
                    {/* <th>Stitch Items</th> */}
                    <th>Status</th>
                    <th>Assigned Karigar</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <EmptyState
                          icon="📋"
                          title={search || filterStatus !== "All" ? "No orders match your filter" : "No orders yet"}
                        />
                      </td>
                    </tr>
                  ) : (
                    paged.map((o) => (
                      <tr key={o.id}>
                        <td
                          className="td-code"
                          style={{ cursor: "pointer" }}
                          onClick={() => navigate("order-detail", { id: o.id })}
                        >
                          {o.id}
                        </td>

                        <td
                          style={{ cursor: "pointer" ,  color: "var(--gold)" }}
                          onClick={() => navigate("customer-detail", { id: o.customerId })}
                        >
                          <strong>{o.customerName}</strong>
                        </td>

                        <td className="td-muted">{fmt(o.orderDate)}</td>
                        {/* <td className="td-muted">{fmt(o.deliveryDate)}</td> */}

                        {/* <td>
                          <div className="tags">
                            {o.stitchDetails?.slice(0, 2).map((s, i) => (
                              <span key={i} className="tag" style={{ fontSize: 11 }}>✂️ {s.qty}× {s.type}</span>
                            ))}
                            {(o.stitchDetails?.length ?? 0) > 2 && (
                              <span className="tag" style={{ fontSize: 11 }}>+{o.stitchDetails.length - 2}</span>
                            )}
                          </div>
                        </td> */}

                        <td><StatusBadge status={o.status} /></td>

                        <td className="td-muted">
                          {o.assignedKarigarName || (
                            <span style={{ color: "var(--red)", fontSize: 11, fontWeight: 600 }}>⚠ Unassigned</span>
                          )}
                        </td>

                        <td style={{ color: "var(--gold)", fontWeight: 600 }}>
                          ₹{o.amount?.toLocaleString()}
                        </td>

                        <td>
                          <div className="td-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate("order-detail", { id: o.id })}>View</button>

                            {/* {o.status !== "Completed" && (
                              <button className="btn btn-ghost btn-sm" onClick={() => navigate("order-edit", { id: o.id })}>Edit</button>
                            )} */}

                            {/* Karigar assignment moved to Karigars → 📤 Assign Work. */}

                            {o.status === "In Progress" && (
                              <button className="btn btn-success btn-sm" onClick={() => handleComplete(o)}>✓ Done</button>
                            )}

                            {o.status === "Completed" && (
                              <button className="btn btn-ghost btn-sm" onClick={() => setReceiptTarget(o)}>🧾 Receipt</button>
                            )}

                            {(() => {
                              // Mirror the refusals Remove_Order.php can be checked for
                              // from list data. Delivered/karigar-assigned items aren't in
                              // this payload, so those still surface as an error toast.
                              const blocked =
                                o.status === "Completed" ? "Order is closed"
                                : (o.received || 0) > 0  ? "Payment already received"
                                : null;
                              return (
                                <button
                                  className="btn btn-danger btn-sm"
                                  disabled={!!blocked}
                                  style={blocked ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                                  title={blocked ?? "Delete this order"}
                                  onClick={() => setDeleteTarget(o)}
                                >
                                  🗑
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination page={page} total={total} setPage={setPage} count={count} perPage={8} />
          </>
        )}
      </div>

      {receiptTarget && (
        <ReceiptModal order={receiptTarget} onClose={() => setReceiptTarget(null)} />
      )}

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete Order"
        message={`Delete order "${deleteTarget?.id}"? This action cannot be undone.`}
      />
    </div>
  );
}
